import React, { useEffect } from 'react';

// manifests

// assets

// services

// props
import { IPaginationResourceShort } from '../../../../../../props/general';

// components
import { DatePicker, DefaultButton, DetailsList, Dropdown, IColumn, IconButton, Link, Panel, PanelType, PrimaryButton, SearchBox, SelectionMode, Stack, TextField } from '@fluentui/react';
import FormPagination, { IFormPage } from '../../../../../uiframeworks/forms/pagination';
import { IVesselRequisitionResourceProps } from '../../../../../../props/vessels/requisitions/requisition';
import GeneralService from '../../../../../../services/general';
import moment from 'moment';
import Label from '../../../../../typography/label';
import Text from '../../../../../typography/text';
import { IVesselRequisitionItemResourceProps, IVesselRequisitionItemResourceShortProps } from '../../../../../../props/vessels/requisitions/item';
import { IQuantityUnitResourceShortProps } from '../../../../../../props/general/quantityUnit';
import SelectVessel from '../../../../../uiframeworks/forms/vessels';
import ValidationService from '../../../../../../services/validation';
import LoadingComponent from '../../../../../feedbacks/loading';
import DataGoodsService from '../../../../../../services/data/goods';
import { IDataGoodsResourceShortProps } from '../../../../../../props/data/goods';

type VesselRequisitionItemSubformProps = {
    item?: Partial<IVesselRequisitionItemResourceProps>;
    options: { quantityUnits: IQuantityUnitResourceShortProps[] };
    onDismissed(data?: IVesselRequisitionItemResourceProps): void;
}

const VesselRequisitionItemSubform: React.FC<VesselRequisitionItemSubformProps> = (props: VesselRequisitionItemSubformProps) => {
    const [keyword, setKeyword] = React.useState<string>("");
    const [searching, setSearching] = React.useState<boolean>(false);
    const [similarItems, setSimilarItems] = React.useState<IDataGoodsResourceShortProps[] | undefined>();

    const [data, setData] = React.useState<Partial<IVesselRequisitionItemResourceProps>>(props.item || { id: GeneralService.guid() });
    const [useExistingGoods, setUseExistingGoods] = React.useState<boolean>(!(props.item?.goods?.id || "").startsWith("new_"));
    const [error, setError] = React.useState<any>({});

    const [step, setStep] = React.useState<'search' | 'form'>(props.item ? 'form' : 'search');
    const mode = props.item ? 'update' : 'create';

    const isSubmitButtonDisabled = () => {
        if ((data.purpose || "").trim() === "" || (data.quantityAmount || "").trim() === "" || !data.quantityUnit) {
            return true;
        } else if (!data.goods) {
            return true;
        } else if (Object.values(error).filter(Boolean).length > 0) {
            return true;
        }

        return false;
    }

    const _onSubmit = () => {
        props.onDismissed(data as IVesselRequisitionItemResourceProps);
    }

    const renderSimilarItemCard = (item: IDataGoodsResourceShortProps) => {
        const hasSize = Number(item.width || "0") !== 0 || Number(item.length || "0") || Number(item.height || "0");
        const hasWeight = Number(item.weightAmount || "0") !== 0;

        return <Stack className={'card clickable'} tokens={{ childrenGap: 8 }} onClick={() => {
            const _data = data;
            _data.goods = {
                ...item, 
                width: Number(item.width || "0") + "",
                length: Number(item.length || "0") + "",
                height: Number(item.height || "0") + "",
                weightAmount: Number(item.weightAmount || "0") + ""
            };

            setUseExistingGoods(true);
            setData({ ..._data });
            setStep('form');
        }}>
            <Label>{item.name.toUpperCase()}</Label>
            <Stack tokens={{ childrenGap: 2 }}>
                {hasSize ? <Stack horizontal tokens={{ childrenGap: 3 }}>
                    <Stack styles={{ root: { width: 100 } }}><Text size={'small'}>Size (P x L x T)</Text></Stack>
                    <Text size={'small'}>:</Text>
                    <Stack horizontal tokens={{ childrenGap: 3 }}>
                        <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.length || "0"))} cm</Text>
                        <Text size={'small'}>x</Text>
                        <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.width || "0"))} cm</Text>
                        <Text size={'small'}>x</Text>
                        <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.height || "0"))} cm</Text>
                    </Stack>
                </Stack> : null}
                {hasWeight ? <Stack horizontal tokens={{ childrenGap: 3 }}>
                    <Stack styles={{ root: { width: 100 } }}><Text size={'small'}>Weight per item</Text></Stack>
                    <Text size={'small'}>:</Text>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.weightAmount || "0"))} {item.weightUnit || ""}</Text>
                </Stack> : null}
            </Stack>
        </Stack>
    }

    return <Panel headerText={mode === 'create' ? 'Add Requisition Item' : 'Update Requisition Item'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text={mode === 'create' ? 'Create' : 'Update'} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed() }} />
            </Stack>;
        }}>
        {step === 'search' ? <Stack tokens={{ childrenGap: 10 }}>
            <Text>Type in item name to check whether the requested items is already in the system.</Text>
            <SearchBox required
                onChange={(evt, value) => setKeyword(value || "")}
                onSearch={async (keyword) => {
                    if ((keyword || "").trim() !== "") {
                        setSimilarItems(undefined);
                        setSearching(true);

                        const _similar = await DataGoodsService.findSimilar('keyword=' + keyword);
                        setSimilarItems(_similar);
                        setSearching(false);
                    } else {
                        setSimilarItems(undefined);
                        setSearching(false);
                    }
                }} />
            {searching ? <LoadingComponent label={'Searching requested item ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
            {similarItems && similarItems.length < 1 ? <Text size={'small'}>We cannot find this item in the system. <Link onClick={() => {
                const _data = data;
                _data.goods = { id: GeneralService.makeid(20, false, 'new_'), name: keyword || "" };

                setUseExistingGoods(false);
                setData({ ..._data });
                setStep('form');
            }}>Click here</Link> to create this as new item.</Text> : null}
            {similarItems && similarItems.length > 0 ? <>
                <Stack className={'divider'} />
                <Text>We found {similarItems.length} similar items in the system. If your requested item is any of this below, click on the item card.</Text>
                <Stack>
                    {similarItems.map((i) => renderSimilarItemCard(i))}
                </Stack>
                <Stack className={'divider'} />
                <Text size={'small'}>None of similar items is your requested item? <Link onClick={() => {
                    const _data = data;
                    _data.goods = { id: GeneralService.makeid(20, false, 'new_'), name: keyword || "" };

                    setUseExistingGoods(false);
                    setData({ ..._data });
                    setStep('form');
                }}>Click here</Link> to create this as new item.</Text>
            </> : null}
        </Stack> : null}
        {step === 'form' ? <Stack tokens={{ childrenGap: 20 }}>
            <TextField value={data.goods?.name || ""}
                label={'Name'}
                required
                disabled={useExistingGoods}
                onChange={(evt, value) => {
                    let _data = data;
                    _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: value || "" };
                    _data.goods.name = value || "";

                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                    error.name = validation.message;

                    setData({ ..._data });
                    setError({ ...error });
                }} />
            <TextField value={data.goods?.description || ""}
                label={'Description'}
                multiline
                rows={2}
                autoAdjustHeight
                resizable={false}
                disabled={useExistingGoods}
                onChange={(evt, value) => {
                    let _data = data;
                    _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                    _data.goods.description = value || "";

                    const validation = ValidationService.combination(value, ['limit'], { maxChars: 1000 });
                    error.description = validation.message;

                    setData({ ..._data });
                    setError({ ...error });
                }} />
            <Stack className={'divider'} />
            <Stack horizontal tokens={{ childrenGap: 10 }}>
                <Stack tokens={{ childrenGap: 6 }} styles={{ root: { width: '75%' } }}>
                    <Label size={'xsmall'}>Size (length x width x height)</Label>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <TextField value={data.goods?.length || ""}
                            suffix={'cm'}
                            disabled={useExistingGoods}
                            styles={{ root: { width: '33.3%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    let _data = data;
                                    _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                                    _data.goods.length = value || "";

                                    setData({ ..._data });
                                }
                            }} />
                        <Text style={{ marginTop: 7 }}>x</Text>
                        <TextField value={data.goods?.width}
                            suffix={'cm'}
                            disabled={useExistingGoods}
                            styles={{ root: { width: '33.3%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    let _data = data;
                                    _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                                    _data.goods.width = value || "";
                                    setData({ ..._data });
                                }
                            }} />
                        <Text style={{ marginTop: 7 }}>x</Text>
                        <TextField value={data.goods?.height}
                            suffix={'cm'}
                            disabled={useExistingGoods}
                            styles={{ root: { width: '33.3%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    let _data = data;
                                    _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                                    _data.goods.height = value || "";
                                    setData({ ..._data });
                                }
                            }} />
                    </Stack>
                </Stack>
                <Stack styles={{ root: { width: '25%' } }}>
                    <TextField label={"Diameter"}
                        value={data.goods?.diameter}
                        suffix={'cm'}
                        disabled={useExistingGoods}
                        styles={{ root: { width: '100%' } }}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                let _data = data;
                                _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                                _data.goods.diameter = value || "";
                                setData({ ..._data });
                            }
                        }} />
                </Stack>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'}>Weight</Label>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Stack grow={1}>
                        <TextField value={data.goods?.weightAmount}
                            styles={{ root: { Width: '100%' } }}
                            disabled={useExistingGoods}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    let _data = data;
                                    _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                                    _data.goods.weightAmount = value || "";
                                    setData({ ..._data });
                                }
                            }} />
                    </Stack>
                    <Dropdown selectedKey={data.goods?.weightUnit}
                        options={[
                            { key: 'ton', text: 'ton' },
                            { key: 'kg', text: 'kg' },
                            { key: 'gr', text: 'gr' },
                            { key: 'mg', text: 'mg' }
                        ]}
                        styles={{ root: { width: 150 } }}
                        disabled={useExistingGoods}
                        onChange={(evt, opt) => {
                            let _data = data;
                            _data.goods = _data.goods || { id: GeneralService.makeid(20, false, 'new_'), name: "" };
                            _data.goods.weightUnit = opt?.key as string;

                            setData({ ..._data });
                        }} />
                </Stack>
            </Stack>
            <Stack className={'divider'} />
            <TextField multiline
                label={'Notes / Purpose'}
                value={data.purpose}
                rows={3}
                autoAdjustHeight
                resizable={false}
                required
                disabled={false}
                onChange={(evt, value) => {
                    let _data = data;
                    _data.purpose = value || "";

                    const validation = ValidationService.combination(_data.purpose, ['required', 'limit'], { maxChars: 2000 });
                    error.purpose = validation.message;

                    setData({ ..._data });
                    setError({ ...error });
                }} />
            <Stack tokens={{ childrenGap: 2 }}>
                <Label required size={'xsmall'}>Quantity</Label>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Stack grow={1}>
                        <TextField value={data.quantityAmount}
                            styles={{ root: { Width: '100%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    let _data = data;
                                    _data.quantityAmount = (value || "").trim();
                                    setData({ ..._data });
                                }
                            }} />
                    </Stack>
                    <Dropdown selectedKey={data.quantityUnit?.id}
                        options={props.options.quantityUnits.map((qu) => { return { key: qu.id, text: qu.name } })}
                        styles={{ root: { width: 150 } }}
                        onChange={(evt, opt) => {
                            let _data = data;
                            const unit = props.options.quantityUnits.find((qu) => qu.id === opt?.key as string);
                            _data.quantityUnit = unit;
                            setData({ ..._data });
                        }} />
                </Stack>
            </Stack>
        </Stack> : null}
    </Panel>;
};

export default VesselRequisitionItemSubform;
